import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useCurrency } from '../context/CurrencyContext';
import { Layout } from '../components/Layout';
import { timesheetAPI, staffAPI, timeClockAPI, aiAPI, timeCorrectionsAPI } from '../lib/api';
import { markOnboarding } from '../components/OnboardingChecklist';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { formatTime } from '../lib/utils';
import {
  Clock,
  Download,
  Loader2,
  Users,
  ClipboardList,
  FileText,
  TrendingUp,
  FileSpreadsheet,
  DollarSign,
  Check,
  X,
  Pencil,
  Plus,
  Sparkles,
  ChevronDown,
  ChevronRight,
  BookOpen,
  AlertCircle,
} from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, Cell, ResponsiveContainer } from 'recharts';

// ── constants ────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];
function isoDate(d) { return d.toISOString().split('T')[0]; }

function getPresetRange(preset) {
  const now = new Date();
  switch (preset) {
    case 'this_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { startDate: isoDate(mon), endDate: isoDate(sun) };
    }
    case 'last_week': {
      const mon = new Date(now);
      mon.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      return { startDate: isoDate(mon), endDate: isoDate(sun) };
    }
    case 'biweekly': {
      const start = new Date(now);
      start.setDate(now.getDate() - 13);
      return { startDate: isoDate(start), endDate: today() };
    }
    case 'this_month': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { startDate: isoDate(first), endDate: isoDate(last) };
    }
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: isoDate(first), endDate: isoDate(last) };
    }
    default:
      return null;
  }
}

const PRESETS = [
  { key: 'this_week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'biweekly', label: 'Bi-weekly' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

const DEFAULT_START = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const DEFAULT_END = today();

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtHours(h) {
  if (!h && h !== 0) return '—';
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
}

// Convert ISO UTC → datetime-local input value (local time)
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert datetime-local input (local time) → ISO UTC string
function localInputToISO(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    pending_approval: 'bg-amber-100 text-amber-800',
    approved: 'bg-blue-100 text-blue-800',
    rejected: 'bg-red-100 text-red-800',
  };
  const labels = {
    active: 'In Progress',
    pending_approval: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {labels[status] || status}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── Compute per-employee overtime risk objects from a flat entries array ──────
function buildOvertimeRisks(entries) {
  const today = new Date().toISOString().split('T')[0];
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekStartStr = weekStart.toISOString().split('T')[0];

  const byEmployee = {};
  entries.forEach(e => {
    const key = e.employee_id || e.employee_name;
    if (!byEmployee[key]) {
      byEmployee[key] = { name: e.employee_name, hours_today: 0, hours_week: 0, missing_clockout: false };
    }
    const hrs = e.duration_hours || 0;
    if (e.date === today) {
      byEmployee[key].hours_today = Math.max(byEmployee[key].hours_today, hrs);
      if (!e.clock_out) byEmployee[key].missing_clockout = true;
    }
    if (e.date >= weekStartStr) byEmployee[key].hours_week += hrs;
  });

  return Object.values(byEmployee)
    .filter(emp => emp.missing_clockout || emp.hours_today > 8 || emp.hours_week > 40)
    .map(emp => ({
      name: emp.name,
      hours_today: Math.round(emp.hours_today * 100) / 100,
      hours_week: Math.round(emp.hours_week * 100) / 100,
      risk_level: emp.missing_clockout ? 'missing_clockout'
        : emp.hours_week > 40 ? 'weekly_ot'
        : emp.hours_today > 10 ? 'long_shift'
        : 'near_ot',
    }))
    .sort((a, b) => {
      const order = { missing_clockout: 0, weekly_ot: 1, long_shift: 2, near_ot: 3 };
      return (order[a.risk_level] ?? 3) - (order[b.risk_level] ?? 3);
    });
}

export default function Timesheets() {
  const { canManage, user } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();

  // Mark onboarding step when user visits Timesheets
  useEffect(() => {
    const uid = user?.id || user?._id || user?.email;
    markOnboarding(uid, 'timesheet');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, setFilters] = useState({
    startDate: DEFAULT_START,
    endDate: DEFAULT_END,
    employeeId: '',
    userRole: '',
    status: '',
  });
  const [datePreset, setDatePreset] = useState('custom');

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ employees: [], totals: {} });
  const [payrollData, setPayrollData] = useState({ employees: [], period_totals: {} });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [exporting, setExporting] = useState('');
  const [scanning, setScanning] = useState(false);
  const [highlightRisks, setHighlightRisks] = useState(false);

  // Action state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editModal, setEditModal] = useState({ open: false, entry: null });
  const [rejectModal, setRejectModal] = useState({ open: false, entry: null });
  const [actionLoading, setActionLoading] = useState('');
  // Manual Entry
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({ employee_id: '', date: '', hours_worked: '', note: '' });
  const [submittingManual, setSubmittingManual] = useState(false);

  // Time Corrections (manager review of missed-punch requests)
  const [corrections, setCorrections] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);
  const [correctionStatusFilter, setCorrectionStatusFilter] = useState('pending');

  const locId = isAllLocations ? null : selectedLocationId;

  const buildParams = useCallback((f) => {
    const p = { start_date: f.startDate, end_date: f.endDate };
    if (locId) p.location_id = locId;
    if (f.employeeId) p.employee_id = f.employeeId;
    if (f.userRole) p.user_role = f.userRole;
    if (f.status) p.status = f.status;
    return p;
  }, [locId]);

  const buildPayrollParams = useCallback((f) => {
    const p = { start_date: f.startDate, end_date: f.endDate };
    if (locId) p.location_id = locId;
    if (f.employeeId) p.employee_id = f.employeeId;
    if (f.userRole) p.user_role = f.userRole;
    return p;
  }, [locId]);

  const fetchDetails = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = buildParams(f);
      const [entriesRes, summaryRes] = await Promise.all([
        timesheetAPI.getEntries(params),
        timesheetAPI.getSummary(params),
      ]);
      setEntries(entriesRes.data || []);
      setSummary(summaryRes.data || { employees: [], totals: {} });
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchPayroll = useCallback(async (f) => {
    if (!canManage) return;
    setPayrollLoading(true);
    try {
      const res = await timesheetAPI.getPayrollSummary(buildPayrollParams(f));
      setPayrollData(res.data || { employees: [], period_totals: {} });
    } catch {
      toast.error('Failed to load payroll summary');
    } finally {
      setPayrollLoading(false);
    }
  }, [buildPayrollParams, canManage]);

  const fetchCorrections = useCallback(async () => {
    if (!canManage) return;
    setCorrectionsLoading(true);
    try {
      const res = await timeCorrectionsAPI.list('tenant');
      setCorrections(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load correction requests');
    } finally {
      setCorrectionsLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    if (canManage) {
      staffAPI.getWithLocations().then(r => setEmployees(r.data || [])).catch(() => {});
      fetchPayroll(filters);
      fetchCorrections();
    }
    fetchDetails(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, isAllLocations]);

  const applyPreset = (preset) => {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const range = getPresetRange(preset);
    if (range) {
      const newFilters = { ...filters, ...range };
      setFilters(newFilters);
      fetchDetails(newFilters);
      if (canManage) fetchPayroll(newFilters);
    }
  };

  const handleApply = () => {
    setDatePreset('custom');
    fetchDetails(filters);
    if (canManage) fetchPayroll(filters);
  };

  // ── action handlers ──────────────────────────────────────────────────────

  const doRefreshEntries = async (f) => {
    try {
      const params = buildParams(f);
      const [entriesRes, summaryRes] = await Promise.all([
        timesheetAPI.getEntries(params),
        timesheetAPI.getSummary(params),
      ]);
      setEntries(entriesRes.data || []);
      setSummary(summaryRes.data || { employees: [], totals: {} });
      setSelectedIds(new Set());
      if (canManage) {
        timesheetAPI.getPayrollSummary(buildPayrollParams(f))
          .then(r => setPayrollData(r.data || { employees: [], period_totals: {} }))
          .catch(() => {});
      }
    } catch {
      toast.error('Failed to reload data');
    }
  };

  const handleApproveEntry = async (entryId) => {
    setActionLoading(entryId);
    try {
      await timeClockAPI.approveShift(entryId);
      toast.success('Entry approved');
      await doRefreshEntries(filters);
    } catch {
      toast.error('Failed to approve entry');
    } finally {
      setActionLoading('');
    }
  };

  const handleRejectEntry = async (entryId, reason) => {
    setActionLoading(entryId);
    try {
      await timeClockAPI.rejectShift(entryId, { reason: reason || null });
      toast.success('Entry rejected');
      setRejectModal({ open: false, entry: null });
      await doRefreshEntries(filters);
    } catch {
      toast.error('Failed to reject entry');
    } finally {
      setActionLoading('');
    }
  };

  const handleEditEntry = async (entryId, data) => {
    setActionLoading(entryId);
    try {
      await timeClockAPI.editShift(entryId, data);
      toast.success('Entry updated — reset to Pending Approval');
      setEditModal({ open: false, entry: null });
      await doRefreshEntries(filters);
    } catch {
      toast.error('Failed to update entry');
    } finally {
      setActionLoading('');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('bulk');
    try {
      const res = await timesheetAPI.bulkApprove({ ids: Array.from(selectedIds) });
      const count = res.data?.approved_count ?? selectedIds.size;
      toast.success(`${count} entr${count !== 1 ? 'ies' : 'y'} approved`);
      await doRefreshEntries(filters);
    } catch {
      toast.error('Bulk approve failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleManualEntry = async (e) => {
    e.preventDefault();
    if (!manualForm.employee_id || !manualForm.date || !manualForm.hours_worked) return;
    setSubmittingManual(true);
    try {
      await timesheetAPI.createManualEntry({
        employee_id: manualForm.employee_id,
        date: manualForm.date,
        hours_worked: parseFloat(manualForm.hours_worked),
        note: manualForm.note || undefined,
      });
      toast.success('Manual entry added and auto-approved');
      setShowManualModal(false);
      setManualForm({ employee_id: '', date: '', hours_worked: '', note: '' });
      await doRefreshEntries(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add manual entry');
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllPending = () => {
    const pendingIds = entries
      .filter(e => e.status === 'pending_approval')
      .map(e => e.id);
    setSelectedIds(prev => {
      const allSelected = pendingIds.length > 0 && pendingIds.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(pendingIds);
    });
  };

  // ── exports (detail) ──────────────────────────────────────────────────────

  const handleExportCSV = async () => {
    setExporting('csv');
    try {
      const res = await timesheetAPI.exportCSV(buildParams(filters));
      triggerDownload(res.data, `timesheets_${filters.startDate}_${filters.endDate}.csv`, 'text/csv');
      toast.success('CSV downloaded');
    } catch { toast.error('CSV export failed'); } finally { setExporting(''); }
  };

  const handleExportRaw = async () => {
    setExporting('raw');
    try {
      const res = await timesheetAPI.exportRaw(buildParams(filters));
      triggerDownload(res.data, `timesheets_export_${filters.startDate}_${filters.endDate}.csv`, 'text/csv');
      toast.success('Raw CSV downloaded');
    } catch { toast.error('Export failed'); } finally { setExporting(''); }
  };

  const handleExportFormatted = async () => {
    setExporting('formatted');
    try {
      const res = await timesheetAPI.exportFormatted(buildParams(filters));
      triggerDownload(res.data, `timesheets_formatted_${filters.startDate}_${filters.endDate}.csv`, 'text/csv');
      toast.success('Formatted CSV downloaded');
    } catch { toast.error('Export failed'); } finally { setExporting(''); }
  };

  const handleExportExcel = async () => {
    setExporting('excel');
    try {
      const res = await timesheetAPI.exportExcel(buildParams(filters));
      triggerDownload(res.data, `timesheets_${filters.startDate}_${filters.endDate}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      toast.success('Excel downloaded');
    } catch { toast.error('Excel export failed'); } finally { setExporting(''); }
  };

  const handleExportPDF = async () => {
    setExporting('pdf');
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(16); doc.setTextColor(30, 41, 59);
      doc.text('Timesheet Report', 14, 16);
      doc.setFontSize(10); doc.setTextColor(100, 116, 139);
      doc.text(`Period: ${filters.startDate}  to  ${filters.endDate}`, 14, 24);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, 30);
      const total = entries.reduce((s, e) => s + (e.duration_hours || 0), 0);
      doc.text(`Total: ${total.toFixed(2)} hrs  |  ${entries.length} shifts`, 14, 36);
      autoTable(doc, {
        head: [['Employee', 'Location', 'Date', 'Clock In', 'Clock Out', 'Hours', 'Status']],
        body: entries.map(e => [
          e.employee_name, e.location_name, e.date,
          e.clock_in ? new Date(e.clock_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—',
          e.clock_out ? new Date(e.clock_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—',
          e.duration_hours ? `${e.duration_hours.toFixed(2)}h` : '—',
          e.status === 'active' ? 'In Progress' : e.status === 'pending_approval' ? 'Pending' : e.status === 'approved' ? 'Approved' : 'Rejected',
        ]),
        foot: [['', '', '', '', 'Total', `${total.toFixed(2)}h`, '']],
        startY: 42,
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold', fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: { fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
      });
      doc.save(`timesheets_${filters.startDate}_${filters.endDate}.pdf`);
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err); toast.error('PDF export failed');
    } finally { setExporting(''); }
  };

  const handleExportPNG = async () => {
    setExporting('png');
    try {
      const { default: html2canvas } = await import('html2canvas');

      const MAX_ROWS = 30;
      const rows     = entries.slice(0, MAX_ROWS);
      const truncated = entries.length > MAX_ROWS;
      const total    = entries.reduce((s, e) => s + (e.duration_hours || 0), 0);
      const today    = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const TH        = 'padding:10px 12px;text-align:left;font-weight:600;font-size:12px;';
      const TH_R      = 'padding:10px 12px;text-align:right;font-weight:600;font-size:12px;';
      const TD        = 'padding:9px 12px;text-align:left;font-size:12px;color:#334155;border-bottom:1px solid #e2e8f0;';
      const TD_R      = 'padding:9px 12px;text-align:right;font-size:12px;color:#334155;border-bottom:1px solid #e2e8f0;';

      const rowsHtml = rows.map((e, i) => {
        const bg      = i % 2 === 0 ? '#ffffff' : '#f8fafc';
        const clockIn = e.clock_in  ? new Date(e.clock_in).toLocaleTimeString('en-US',  { hour: '2-digit', minute: '2-digit' }) : '—';
        const clockOut= e.clock_out ? new Date(e.clock_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—';
        const hours   = e.duration_hours ? `${e.duration_hours.toFixed(2)}h` : '—';
        return `<tr style="background:${bg};">
          <td style="${TD}">${e.employee_name || '—'}</td>
          <td style="${TD}">${e.date || '—'}</td>
          <td style="${TD}">${clockIn}</td>
          <td style="${TD}">${clockOut}</td>
          <td style="${TD_R}">${hours}</td>
        </tr>`;
      }).join('');

      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1024px;background:#ffffff;padding:32px;font-family:ui-sans-serif,system-ui,sans-serif;box-sizing:border-box;';
      container.innerHTML = `
        <div style="border-bottom:2px solid #1e293b;padding-bottom:14px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-end;">
          <div>
            <h1 style="font-size:20px;font-weight:700;color:#1e293b;margin:0 0 4px;">Timesheet Report</h1>
            <p style="font-size:13px;color:#64748b;margin:0;">Period: ${filters.startDate} &ndash; ${filters.endDate}</p>
          </div>
          <p style="font-size:12px;color:#94a3b8;margin:0;">Generated ${today}</p>
        </div>
        <div style="margin-bottom:16px;display:flex;gap:24px;">
          <span style="font-size:13px;color:#475569;"><strong style="color:#1e293b;">${entries.length}</strong> shifts</span>
          <span style="font-size:13px;color:#475569;">Total: <strong style="color:#1e293b;">${total.toFixed(2)} hrs</strong></span>
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#1e293b;color:#ffffff;">
              <th style="${TH}">Employee</th>
              <th style="${TH}">Date</th>
              <th style="${TH}">Clock In</th>
              <th style="${TH}">Clock Out</th>
              <th style="${TH_R}">Total Hours</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
          <tfoot>
            <tr style="background:#f1f5f9;">
              <td colspan="4" style="padding:9px 12px;text-align:right;font-weight:700;font-size:12px;color:#1e293b;">Total</td>
              <td style="padding:9px 12px;text-align:right;font-weight:700;font-size:12px;color:#1e293b;">${total.toFixed(2)}h</td>
            </tr>
          </tfoot>
        </table>
        ${truncated ? `<p style="margin-top:14px;font-size:11px;color:#94a3b8;text-align:center;">Showing first ${MAX_ROWS} of ${entries.length} rows — export CSV or Excel for complete data.</p>` : ''}
      `;

      document.body.appendChild(container);
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: 1024,
        windowWidth: 1024,
      });
      document.body.removeChild(container);

      const link    = document.createElement('a');
      link.download = `timesheet-${filters.startDate}.png`;
      link.href     = canvas.toDataURL('image/png');
      link.click();
      toast.success('PNG downloaded');
    } catch (err) {
      console.error(err); toast.error('PNG export failed');
    } finally { setExporting(''); }
  };

  // ── exports (payroll) ─────────────────────────────────────────────────────

  const handleExportPayrollCSV = async () => {
    setExporting('payroll-csv');
    try {
      const res = await timesheetAPI.exportPayrollCSV(buildPayrollParams(filters));
      triggerDownload(res.data, `payroll_${filters.startDate}_${filters.endDate}.csv`, 'text/csv');
      toast.success('Payroll CSV downloaded');
    } catch { toast.error('Payroll CSV export failed'); } finally { setExporting(''); }
  };

  const handleExportPayrollExcel = async () => {
    setExporting('payroll-excel');
    try {
      const res = await timesheetAPI.exportPayrollExcel(buildPayrollParams(filters));
      triggerDownload(res.data, `payroll_${filters.startDate}_${filters.endDate}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      toast.success('Payroll Excel downloaded');
    } catch { toast.error('Payroll Excel export failed'); } finally { setExporting(''); }
  };

  function triggerDownload(data, filename, type) {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  }

  // ── derived ───────────────────────────────────────────────────────────────

  const totalHours = entries.reduce((s, e) => s + (e.duration_hours || 0), 0);
  const completedEntries = entries.filter(e => e.clock_out);
  const totals = summary.totals || {};
  const isManager = canManage;
  const pendingCount = entries.filter(e => e.status === 'pending_approval').length;

  // Wage lookup: employee_id → hourly_wage (from payroll summary if loaded)
  const wageMap = useMemo(() => {
    const m = {};
    (payrollData.employees || []).forEach(e => {
      if (e.employee_id) m[e.employee_id] = e.hourly_wage ?? null;
    });
    return m;
  }, [payrollData.employees]);

  // Build name → unavailability periods lookup for DetailsTable badges
  const unavailabilityByName = {};
  employees.forEach(s => {
    if (s.name && (s.unavailability || []).length) {
      unavailabilityByName[s.name] = s.unavailability;
    }
  });

  // Reset risk highlights whenever filters change
  useEffect(() => { setHighlightRisks(false); }, [filters]);

  // ── Scan for Risks with Eva ────────────────────────────────────────────
  const scanForRisks = async () => {
    if (scanning) return;
    setScanning(true);
    setHighlightRisks(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
      const unavailable_staff = employees
        .filter(s => (s.unavailability || []).some(r => r.start_date <= nextWeek && r.end_date >= today))
        .map(s => ({
          name: s.name,
          dates: (s.unavailability || [])
            .filter(r => r.start_date <= nextWeek && r.end_date >= today)
            .map(r => r.start_date === r.end_date ? r.start_date : `${r.start_date} to ${r.end_date}`),
        }));
      const risks = {
        long_shifts:       entries.filter(e => (e.duration_hours || 0) > 10).length,
        near_overtime:     entries.filter(e => (e.duration_hours || 0) > 8).length,
        missing_clockouts: entries.filter(e => !e.clock_out).length,
        total_staff:       entries.length,
        overtime_risks:    buildOvertimeRisks(entries),
        unavailable_staff,
        payroll_summary: payrollData?.period_totals?.total_pay != null ? {
          total_cost: payrollData.period_totals.total_pay,
          overtime_cost: payrollData.period_totals.overtime_pay ?? 0,
          top_employee_cost: [...(payrollData.employees || [])]
            .filter(e => e.total_pay != null)
            .sort((a, b) => (b.total_pay ?? 0) - (a.total_pay ?? 0))
            .map(e => ({ name: e.employee_name, total_pay: e.total_pay }))[0] ?? null,
        } : null,
      };
      const res = await aiAPI.chat(
        'Scan my timesheets and identify workforce risks. Give me 3 specific immediate actions.',
        risks
      );
      window.dispatchEvent(new CustomEvent('eva:open', { detail: res.data.response }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eva is unavailable. Check AI settings.');
    } finally {
      setScanning(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-5 p-4 md:p-6">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}
                data-testid="timesheets-title">
              Timesheets
              {isManager && <HelpTooltip text="Timesheets summarize employee work hours based on clock-in and clock-out records. Managers can review, approve, and export hours for payroll processing." learnMoreUrl="/help/timesheets" />}
            </h1>
            <p className="text-slate-500 mt-0.5 text-sm">
              {isManager ? 'Track clock-in/out records and prepare payroll' : 'Your work hours history'}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Bulk Approve button — visible when entries are selected */}
            {isManager && selectedIds.size > 0 && (
              <Button
                onClick={handleBulkApprove}
                disabled={actionLoading === 'bulk'}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="bulk-approve-btn"
                size="sm"
              >
                {actionLoading === 'bulk'
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <Check className="h-4 w-4 mr-1" />}
                Approve Selected ({selectedIds.size})
              </Button>
            )}

            {/* Add Manual Entry — manager/supervisor only */}
            {isManager && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManualModal(true)}
                data-testid="add-manual-entry-btn"
                className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700 gap-1.5"
              >
                <Plus size={14} />
                Manual Entry
              </Button>
            )}

            {/* Scan for Risks — Eva button, owner/manager only */}
            {isManager && (
              <Button
                variant="outline"
                size="sm"
                onClick={scanForRisks}
                disabled={scanning || loading || entries.length === 0}
                data-testid="scan-for-risks-btn"
                className="border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700 gap-1.5"
              >
                {scanning
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Sparkles size={14} />}
                {scanning ? 'Scanning…' : 'Scan for Risks'}
              </Button>
            )}

            {/* Export buttons */}
            {activeTab === 'details' ? (
              <>
                <Button variant="outline" size="sm" onClick={handleExportCSV}
                  disabled={!!exporting || loading} data-testid="export-csv-btn">
                  {exporting === 'csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportRaw}
                  disabled={!!exporting || loading} data-testid="export-raw-csv-btn"
                  title="Raw export: hours, OT, tips, total pay, break compliance">
                  {exporting === 'raw' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  Raw CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportFormatted}
                  disabled={!!exporting || loading} data-testid="export-formatted-csv-btn"
                  title="Accountant-ready format (regular, OT, total, rate, tips, gross pay, compliance)">
                  {exporting === 'formatted' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  Formatted CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportExcel}
                  disabled={!!exporting || loading} data-testid="export-excel-btn">
                  {exporting === 'excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
                  Excel
                </Button>
                <Button size="sm" onClick={handleExportPDF}
                  disabled={!!exporting || loading} data-testid="export-pdf-btn">
                  {exporting === 'pdf' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPNG}
                  disabled={!!exporting || loading} data-testid="export-png-btn">
                  {exporting === 'png' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  PNG
                </Button>
              </>
            ) : (
              isManager && (
                <>
                  <Button variant="outline" size="sm" onClick={handleExportPayrollCSV}
                    disabled={!!exporting || payrollLoading} data-testid="export-payroll-csv-btn">
                    {exporting === 'payroll-csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                    Export CSV
                  </Button>
                  <Button size="sm" onClick={handleExportPayrollExcel}
                    disabled={!!exporting || payrollLoading} data-testid="export-payroll-excel-btn"
                    className="bg-teal-600 hover:bg-teal-700 text-white">
                    {exporting === 'payroll-excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1" />}
                    Export Payroll
                  </Button>
                </>
              )
            )}
          </div>
          {/* Compatibility note — visible only on the Timesheets details tab */}
          {activeTab === 'details' && (
            <div
              className="flex items-center gap-2 text-[11px] text-slate-400 mt-1 flex-wrap"
              data-testid="csv-compatibility-row"
            >
              <span>Compatible with</span>
              <span className="font-mono font-semibold text-slate-500 grayscale opacity-70 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50">
                QuickBooks
              </span>
              <span className="font-mono font-semibold text-slate-500 grayscale opacity-70 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50">
                Xero
              </span>
              <Link
                to="/help/timesheet-export"
                className="text-orange-500 hover:text-orange-600 font-semibold ml-1"
                data-testid="csv-help-link"
              >
                Learn more
              </Link>
            </div>
          )}
        </div>

        {/* ── date presets ── */}
        <div className="flex gap-1.5 flex-wrap" data-testid="date-presets">
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              data-testid={`preset-${p.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                datePreset === p.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-800'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* ── filters ── */}
        <Card className="border-slate-200" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs" htmlFor="ts-start">Start Date</Label>
                <Input id="ts-start" type="date" value={filters.startDate}
                  onChange={e => { setFilters(f => ({ ...f, startDate: e.target.value })); setDatePreset('custom'); }}
                  data-testid="filter-start-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs" htmlFor="ts-end">End Date</Label>
                <Input id="ts-end" type="date" value={filters.endDate}
                  onChange={e => { setFilters(f => ({ ...f, endDate: e.target.value })); setDatePreset('custom'); }}
                  data-testid="filter-end-date" />
              </div>

              {isManager && (
                <div className="space-y-1">
                  <Label className="text-xs">Employee</Label>
                  <Select value={filters.employeeId || 'all'}
                    onValueChange={v => setFilters(f => ({ ...f, employeeId: v === 'all' ? '' : v, userRole: '' }))}>
                    <SelectTrigger className="h-10 text-sm" data-testid="filter-employee-trigger">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isManager && (
                <div className="space-y-1">
                  <Label className="text-xs">Role</Label>
                  <Select value={filters.userRole || 'all'}
                    onValueChange={v => setFilters(f => ({ ...f, userRole: v === 'all' ? '' : v, employeeId: '' }))}>
                    <SelectTrigger className="h-10 text-sm" data-testid="filter-role-trigger">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={filters.status || 'all'}
                    onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="h-10 text-sm" data-testid="filter-status-trigger">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">In Progress</SelectItem>
                      <SelectItem value="pending_approval">Pending Approval</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={handleApply}
                disabled={loading || payrollLoading}
                data-testid="apply-filters-btn">
                {(loading || payrollLoading) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ── summary cards (details tab only) ── */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-orange-600" size={18} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900" data-testid="stat-total-hours">
                      {loading ? '—' : fmtHours(totalHours)}
                    </p>
                    <p className="text-xs text-slate-500">Total Hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="text-blue-600" size={18} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900" data-testid="stat-total-shifts">
                      {loading ? '—' : completedEntries.length}
                    </p>
                    <p className="text-xs text-slate-500">Completed Shifts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="text-green-600" size={18} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900" data-testid="stat-employees">
                      {loading ? '—' : (totals.employee_count ?? 0)}
                    </p>
                    <p className="text-xs text-slate-500">Employees</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-purple-600" size={18} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900" data-testid="stat-avg-hours">
                      {loading ? '—' : (totals.employee_count
                        ? fmtHours(+(totals.total_hours / totals.employee_count).toFixed(2))
                        : '—'
                      )}
                    </p>
                    <p className="text-xs text-slate-500">Avg Hrs / Employee</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── tabs ── */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="timesheet-tabs">
          <button
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'details'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setActiveTab('details')}
            data-testid="tab-details"
          >
            <ClipboardList size={14} />
            Timesheets
            {isManager && pendingCount > 0 && (
              <span
                className="bg-amber-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none"
                data-testid="pending-count-badge"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
          {isManager && (
            <button
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'payroll'
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('payroll')}
              data-testid="tab-payroll"
            >
              <DollarSign size={14} />
              Payroll Summary
            </button>
          )}
          {isManager && (
            <button
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'analytics'
                  ? 'border-violet-600 text-violet-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('analytics')}
              data-testid="tab-analytics"
            >
              <TrendingUp size={14} />
              Payroll Analytics
            </button>
          )}
          {isManager && (
            <button
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'corrections'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('corrections')}
              data-testid="tab-corrections"
            >
              <AlertCircle size={14} />
              Time Corrections
              {corrections.filter(c => c.status === 'pending').length > 0 && (
                <span
                  className="bg-orange-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none"
                  data-testid="corrections-pending-badge"
                >
                  {corrections.filter(c => c.status === 'pending').length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* ── tab content ── */}
        {activeTab === 'details' && (
          loading
            ? <LoadingSpinner />
            : <DetailsTable
                entries={entries}
                isManager={isManager}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onSelectAllPending={handleSelectAllPending}
                onApprove={handleApproveEntry}
                onReject={(entry) => setRejectModal({ open: true, entry })}
                onEdit={(entry) => setEditModal({ open: true, entry })}
                actionLoading={actionLoading}
                highlightRisks={highlightRisks}
                unavailabilityByName={unavailabilityByName}
              />
        )}
        {activeTab === 'payroll' && (
          payrollLoading
            ? <LoadingSpinner />
            : <PayrollSummaryView
                data={payrollData}
                entries={entries}
                filters={filters}
                onExportCSV={handleExportPayrollCSV}
                onExportExcel={handleExportPayrollExcel}
                exporting={exporting}
              />
        )}
        {activeTab === 'analytics' && (
          (loading || payrollLoading)
            ? <LoadingSpinner />
            : <PayrollAnalyticsView entries={entries} payrollData={payrollData} />
        )}
        {activeTab === 'corrections' && isManager && (
          correctionsLoading
            ? <LoadingSpinner />
            : <CorrectionsView
                corrections={corrections}
                statusFilter={correctionStatusFilter}
                onStatusFilterChange={setCorrectionStatusFilter}
                onRefresh={fetchCorrections}
                onRefreshEntries={() => fetchDetails(filters)}
              />
        )}
      </div>

      {/* ── Modals ── */}
      <EditTimeModal
        open={editModal.open}
        entry={editModal.entry}
        onClose={() => setEditModal({ open: false, entry: null })}
        onSave={handleEditEntry}
        loading={actionLoading !== ''}
        wageMap={wageMap}
      />
      <RejectModal
        open={rejectModal.open}
        entry={rejectModal.entry}
        onClose={() => setRejectModal({ open: false, entry: null })}
        onReject={handleRejectEntry}
        loading={actionLoading !== ''}
      />

      {/* Manual Entry Modal */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6" data-testid="manual-entry-modal">
            <h3 className="font-semibold text-slate-800 text-lg mb-4">Add Manual Entry</h3>
            <p className="text-xs text-slate-500 mb-5">
              Adds an auto-approved timesheet entry marked as manual. Included in the next payroll run.
            </p>
            <form onSubmit={handleManualEntry} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Employee</label>
                <select
                  value={manualForm.employee_id}
                  onChange={e => setManualForm(f => ({ ...f, employee_id: e.target.value }))}
                  required
                  data-testid="manual-employee-select"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select employee…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                <input
                  type="date"
                  value={manualForm.date}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))}
                  required
                  data-testid="manual-date-input"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Hours Worked</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={manualForm.hours_worked}
                  onChange={e => setManualForm(f => ({ ...f, hours_worked: e.target.value }))}
                  required
                  placeholder="e.g. 8"
                  data-testid="manual-hours-input"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Note (optional)</label>
                <input
                  type="text"
                  value={manualForm.note}
                  onChange={e => setManualForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="e.g. Forgot to clock in"
                  data-testid="manual-note-input"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowManualModal(false); setManualForm({ employee_id: '', date: '', hours_worked: '', note: '' }); }}
                  className="flex-1 px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingManual}
                  data-testid="manual-entry-submit-btn"
                  className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {submittingManual ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {submittingManual ? 'Adding…' : 'Add Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ── Payroll Analytics helpers ────────────────────────────────────────────────

function fmtAnalyticsDate(dateStr) {
  return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7)); // Monday-start
  return d.toISOString().split('T')[0];
}

function computeEntryPay(entry, wageMap, otThreshold = 8) {
  const wage = wageMap[entry.employee_id] ?? wageMap[entry.employee_name] ?? null;
  const hours = entry.duration_hours || 0;
  if (!wage || !hours) return { regularPay: 0, otPay: 0, totalPay: 0, otHours: 0, hasWage: !!wage };
  const regular = Math.min(hours, otThreshold);
  const overtime = Math.max(0, hours - otThreshold);
  return {
    regularPay: regular * wage,
    otPay: overtime * wage * 1.5,
    totalPay: regular * wage + overtime * wage * 1.5,
    otHours: overtime,
    hasWage: true,
  };
}

function buildGroups(entries, wageMap, mode, otThreshold) {
  const byKey = {};
  entries.forEach(e => {
    if (!e.date) return;
    const key = mode === 'daily' ? e.date : getWeekStart(e.date);
    if (!byKey[key]) byKey[key] = { key, entries: [], totalCost: 0, otCost: 0, totalHours: 0, staff: new Set() };
    const g = byKey[key];
    g.entries.push(e);
    g.staff.add(e.employee_name);
    const p = computeEntryPay(e, wageMap, otThreshold);
    g.totalCost += p.totalPay;
    g.otCost += p.otPay;
    g.totalHours += e.duration_hours || 0;
  });
  return Object.values(byKey)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(g => ({ ...g, staffCount: g.staff.size }));
}

function buildEmployeeBreakdown(groupEntries, wageMap, otThreshold) {
  const byEmp = {};
  groupEntries.forEach(e => {
    if (!byEmp[e.employee_name]) byEmp[e.employee_name] = { name: e.employee_name, totalHours: 0, otHours: 0, totalPay: 0, hasWage: false };
    const emp = byEmp[e.employee_name];
    const p = computeEntryPay(e, wageMap, otThreshold);
    emp.totalHours += e.duration_hours || 0;
    emp.otHours += p.otHours;
    emp.totalPay += p.totalPay;
    if (p.hasWage) emp.hasWage = true;
  });
  return Object.values(byEmp).sort((a, b) => b.totalPay - a.totalPay || b.totalHours - a.totalHours);
}

// ── Payroll Analytics View ────────────────────────────────────────────────────

function PayrollAnalyticsView({ entries, payrollData }) {
  const { formatMoney } = useCurrency();
  const [view, setView] = useState('daily');
  const [drillKey, setDrillKey] = useState(null);
  const [budget, setBudget] = useState(() => Number(localStorage.getItem('payroll_budget_weekly_v1') || 0));

  const otThreshold = payrollData?.overtime_threshold_hours_per_day ?? 8;

  const wageMap = useMemo(() => {
    const map = {};
    (payrollData?.employees || []).forEach(e => {
      if (e.employee_id) map[e.employee_id] = e.hourly_wage;
      if (e.employee_name) map[e.employee_name] = e.hourly_wage;
    });
    return map;
  }, [payrollData]);

  const groups = useMemo(
    () => buildGroups(entries, wageMap, view, otThreshold),
    [entries, wageMap, view, otThreshold]
  );

  const hasWageData = (payrollData?.employees || []).some(e => e.hourly_wage != null);

  if (entries.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-16 text-center">
          <DollarSign className="mx-auto h-12 w-12 text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No timesheet data to analyze</p>
          <p className="text-slate-400 text-sm mt-1">Adjust the date range to see payroll analytics</p>
        </CardContent>
      </Card>
    );
  }

  const btnBase = 'px-3 py-1 text-xs font-medium rounded transition-colors';
  const totalRows = groups.length;

  return (
    <div className="space-y-4" data-testid="payroll-analytics-view">
      {!hasWageData && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="analytics-no-wage-banner">
          <span className="shrink-0 mt-0.5">⚠</span>
          <span>Set hourly wages in <a href="/staff" className="underline font-medium">Staff</a> to see cost calculations.</span>
        </div>
      )}

      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid="budget-input-section">
        <label htmlFor="weekly-budget" className="text-sm text-slate-500 whitespace-nowrap">Weekly Budget ($)</label>
        <input
          id="weekly-budget"
          type="number"
          min="0"
          step="100"
          value={budget || ''}
          onChange={(e) => { const v = Number(e.target.value) || 0; setBudget(v); localStorage.setItem('payroll_budget_weekly_v1', v); }}
          placeholder="0"
          className="w-32 px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          data-testid="budget-input"
        />
        {budget > 0 && <span className="text-xs text-slate-400">Variance shown in weekly view</span>}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base font-semibold text-slate-800">
              Payroll Drill-Down
              <span className="ml-2 text-xs font-normal text-slate-500">Click any row to see employee breakdown</span>
            </CardTitle>
            <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5 bg-slate-50">
              <button
                className={`${btnBase} ${view === 'daily' ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => { setView('daily'); setDrillKey(null); }}
                data-testid="analytics-toggle-daily"
              >
                Daily
              </button>
              <button
                className={`${btnBase} ${view === 'weekly' ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => { setView('weekly'); setDrillKey(null); }}
                data-testid="analytics-toggle-weekly"
              >
                Weekly
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">{view === 'daily' ? 'Date' : 'Week'}</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Staff</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Total Hours</th>
                  {hasWageData && (
                    <>
                      <th className="text-center px-4 py-3 font-medium text-teal-700">Total Cost</th>
                      <th className="text-center px-4 py-3 font-medium text-amber-700">OT Cost</th>
                    </>
                  )}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {groups.map(g => {
                  const isOpen = drillKey === g.key;
                  const label = view === 'daily'
                    ? fmtAnalyticsDate(g.key)
                    : (() => {
                        const wkEnd = new Date(g.key + 'T12:00:00Z');
                        wkEnd.setUTCDate(wkEnd.getUTCDate() + 6);
                        return `${fmtAnalyticsDate(g.key)} – ${fmtAnalyticsDate(wkEnd.toISOString().split('T')[0])}`;
                      })();
                  const empRows = isOpen ? buildEmployeeBreakdown(g.entries, wageMap, otThreshold) : [];

                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className={`border-b border-slate-100 cursor-pointer transition-colors select-none ${isOpen ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                        onClick={() => setDrillKey(k => k === g.key ? null : g.key)}
                        data-testid={`analytics-row-${g.key}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-900">{label}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{g.staffCount}</td>
                        <td className="px-4 py-3 text-center text-slate-600">{fmtHours(g.totalHours)}</td>
                        {hasWageData && (
                          <>
                            <td className="px-4 py-3 text-center font-semibold text-teal-700">
                              {g.totalCost > 0 ? formatMoney(g.totalCost) : '—'}
                              {view === 'weekly' && budget > 0 && g.totalCost > 0 && (() => {
                                const variance = g.totalCost - budget;
                                const pct = ((variance / budget) * 100).toFixed(1);
                                const isOver = variance > 0;
                                return (
                                  <div className="mt-1 space-y-0.5 text-xs font-normal" data-testid={`budget-variance-${g.key}`}>
                                    <div className="text-slate-400">Budget: {formatMoney(budget)}</div>
                                    <div className={`font-semibold ${isOver ? 'text-red-600' : 'text-emerald-600'}`}>
                                      {isOver ? '+' : ''}{formatMoney(variance)} ({isOver ? '+' : ''}{pct}%)
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 text-center font-medium text-amber-700">
                              {g.otCost > 0 ? formatMoney(g.otCost) : '—'}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-3 text-slate-400 text-center">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="border-b border-slate-200 bg-slate-50/60" data-testid={`drill-down-${g.key}`}>
                          <td colSpan={hasWageData ? 6 : 4} className="px-6 pb-4 pt-2">
                            <div className="rounded-lg border border-slate-200 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-slate-800 text-white">
                                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                                    <th className="text-center px-3 py-2 font-medium">Hours</th>
                                    <th className="text-center px-3 py-2 font-medium">OT Hours</th>
                                    {hasWageData && <th className="text-center px-3 py-2 font-medium">Total Pay</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {empRows.map((emp, i) => (
                                    <tr key={i} className="border-t border-slate-100 hover:bg-white transition-colors" data-testid={`drill-down-row-${i}`}>
                                      <td className="px-3 py-2 font-medium text-slate-900">{emp.name}</td>
                                      <td className="px-3 py-2 text-center text-slate-600">{fmtHours(emp.totalHours)}</td>
                                      <td className="px-3 py-2 text-center text-amber-700 font-medium">
                                        {emp.otHours > 0 ? fmtHours(emp.otHours) : '—'}
                                      </td>
                                      {hasWageData && (
                                        <td className="px-3 py-2 text-center font-semibold text-teal-700">
                                          {emp.hasWage ? formatMoney(emp.totalPay) : '—'}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td className="px-4 py-3 font-bold text-slate-800">
                    Total ({totalRows} {view === 'daily' ? 'day' : 'week'}{totalRows !== 1 ? 's' : ''})
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-slate-800">
                    {new Set(entries.map(e => e.employee_name)).size}
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-slate-800">
                    {fmtHours(groups.reduce((s, g) => s + g.totalHours, 0))}
                  </td>
                  {hasWageData && (
                    <>
                      <td className="px-4 py-3 text-center font-bold text-teal-700">
                        {formatMoney(groups.reduce((s, g) => s + g.totalCost, 0))}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-amber-700">
                        {formatMoney(groups.reduce((s, g) => s + g.otCost, 0))}
                      </td>
                    </>
                  )}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── shared helpers ────────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );
}

// ── Timesheet Details table ───────────────────────────────────────────────────

// Returns true if `date` (YYYY-MM-DD) falls within any unavailability period
function isDateUnavailable(periods, date) {
  if (!periods || !date) return false;
  return periods.some(p => p.start_date <= date && date <= p.end_date);
}

function DetailsTable({
  entries,
  isManager,
  selectedIds = new Set(),
  onToggleSelect,
  onSelectAllPending,
  onApprove,
  onReject,
  onEdit,
  actionLoading,
  highlightRisks = false,
  unavailabilityByName = {},
}) {
  const { formatMoney } = useCurrency();
  if (entries.length === 0) {
    return (
      <Card className="border-slate-200" data-testid="timesheets-empty-state">
        <CardContent className="py-16 text-center">
          <Clock className="mx-auto h-12 w-12 text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No timesheet records found</p>
          <p className="text-slate-400 text-sm mt-1 mb-4">
            {isManager
              ? 'Shifts recorded via Time Clock will appear here'
              : 'Your clock-in and clock-out records will appear here'}
          </p>
          {isManager && (
            <a href="/time-clock">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="timesheets-empty-cta">
                + Go to Time Clock
              </Button>
            </a>
          )}
        </CardContent>
      </Card>
    );
  }

  const pendingEntries = entries.filter(e => e.status === 'pending_approval');
  const allPendingSelected = pendingEntries.length > 0 && pendingEntries.every(e => selectedIds.has(e.id));

  // Compute per-employee weekly hours from loaded entries (for >40h/week badge)
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekStartStr = weekStart.toISOString().split('T')[0];
  const weeklyHoursMap = {};
  entries.forEach(e => {
    if (!e.date || e.date < weekStartStr) return;
    const key = e.employee_id || e.employee_name;
    weeklyHoursMap[key] = (weeklyHoursMap[key] || 0) + (e.duration_hours || 0);
  });

  return (
    <Card className="border-slate-200" data-testid="details-table-card">
      <CardHeader className="pb-2 px-4 pt-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-slate-600">
            {entries.length} record{entries.length !== 1 ? 's' : ''}
          </CardTitle>
          {isManager && pendingEntries.length > 0 && (
            <button
              onClick={onSelectAllPending}
              className="text-xs text-slate-500 hover:text-orange-600 transition-colors"
              data-testid="select-all-pending-btn"
            >
              {allPendingSelected
                ? `Deselect all (${pendingEntries.length})`
                : `Select all pending (${pendingEntries.length})`}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {highlightRisks && (
          <div className="flex flex-wrap items-center gap-3 text-sm mb-2 px-1" data-testid="risk-legend">
            <span className="text-slate-500 font-medium mr-1">Risk indicators:</span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
              <span className="text-slate-600">Missing clock-out</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-orange-500 flex-shrink-0"></span>
              <span className="text-slate-600">Long shift (&gt;10h)</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0"></span>
              <span className="text-slate-600">Near overtime (&gt;8h)</span>
            </span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="details-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {isManager && <th className="px-3 py-3 w-10" />}
                <th className="text-left px-4 py-3 font-medium text-slate-600">Employee</th>
                {isManager && <th className="text-left px-4 py-3 font-medium text-slate-600">Location</th>}
                <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Clock In</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Clock Out</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Hours</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Tips</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                {isManager && <th className="text-left px-4 py-3 font-medium text-slate-600">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => {
                const isPending = e.status === 'pending_approval';
                const isApproved = e.status === 'approved';
                const isRejected = e.status === 'rejected';
                const isSelected = selectedIds.has(e.id);
                const isActioning = actionLoading === e.id;
                const canEdit = (isPending || isApproved || isRejected) && !!e.clock_out;

                // Risk highlight (priority: missing clock-out > long shift > near overtime)
                let riskClass = '';
                if (highlightRisks) {
                  if (!e.clock_out) {
                    riskClass = 'bg-red-50 border-l-4 border-red-400';
                  } else if ((e.duration_hours || 0) > 10) {
                    riskClass = 'bg-orange-50 border-l-4 border-orange-400';
                  } else if ((e.duration_hours || 0) > 8) {
                    riskClass = 'bg-yellow-50 border-l-4 border-yellow-400';
                  }
                }

                return (
                  <tr
                    key={e.id || idx}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-orange-50/60' : ''} ${riskClass}`}
                    data-testid={`timesheet-row-${idx}`}
                  >
                    {isManager && (
                      <td className="px-3 py-3 text-center">
                        {isPending ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleSelect(e.id)}
                            className="rounded border-slate-300 cursor-pointer accent-orange-500"
                            data-testid={`select-entry-${idx}`}
                          />
                        ) : (
                          <span className="w-4 h-4 inline-block" />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-slate-900">{e.employee_name}</td>
                    {isManager && <td className="px-4 py-3 text-slate-600">{e.location_name}</td>}
                    <td className="px-4 py-3 text-slate-600">{e.date}</td>
                    <td className="px-4 py-3 text-slate-600">{e.clock_in ? formatTime(e.clock_in) : '—'}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {e.clock_out
                        ? formatTime(e.clock_out)
                        : <span className="text-green-600 text-xs font-medium">Active</span>}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <span className="flex items-center gap-1.5 flex-wrap">
                        {e.duration_hours > 0 ? fmtHours(e.duration_hours) : '—'}
                        {e.is_manual && (
                          <span className="text-xs text-slate-400" data-testid={`manual-label-${idx}`}>(Manual)</span>
                        )}
                        {!e.clock_out && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700 whitespace-nowrap" data-testid={`risk-label-missing-${idx}`}>Missing Out</span>
                        )}
                        {e.clock_out && (e.duration_hours || 0) > 10 && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-orange-100 text-orange-700 whitespace-nowrap" data-testid={`risk-label-ot-${idx}`}>Overtime</span>
                        )}
                        {e.clock_out && (e.duration_hours || 0) > 8 && (e.duration_hours || 0) <= 10 && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-yellow-100 text-yellow-700 whitespace-nowrap" data-testid={`risk-label-near-${idx}`}>OT Risk</span>
                        )}
                        {(weeklyHoursMap[e.employee_id || e.employee_name] || 0) > 40 && e.date >= weekStartStr && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-800 whitespace-nowrap" data-testid={`risk-label-weekly-${idx}`}>&gt;40h/wk</span>
                        )}
                        {isDateUnavailable(unavailabilityByName[e.employee_name], e.date) && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-purple-100 text-purple-700 whitespace-nowrap" data-testid={`unavailable-label-${idx}`}>Unavailable</span>
                        )}
                        {e.break_compliance && !e.break_compliance.is_compliant && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700 whitespace-nowrap"
                            title={`Required ${e.break_compliance.required_break} min, taken ${e.break_compliance.taken_break} min`}
                            data-testid={`risk-label-missed-break-${idx}`}
                          >
                            Missed break ({e.break_compliance.missed_break} min)
                          </span>
                        )}
                        {e.kpi && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${
                              e.kpi.status === 'GREEN'
                                ? 'bg-green-100 text-green-700'
                                : e.kpi.status === 'YELLOW'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                            title={`${e.kpi.bonus_eligible ? 'Bonus eligible' : 'Not bonus eligible'}`}
                            data-testid={`kpi-badge-${idx}`}
                          >
                            KPI {e.kpi.kpi_score} ({e.kpi.status})
                          </span>
                        )}
                        {e.kpi && e.kpi.bonus_eligible && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-semibold whitespace-nowrap bg-violet-100 text-violet-700"
                            title="KPI bonus added to payroll"
                            data-testid={`bonus-badge-${idx}`}
                          >
                            + $5 bonus
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-testid={`tips-cell-${idx}`}>
                      {e.tips_amount > 0 ? formatMoney(e.tips_amount) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-0.5">
                        <StatusBadge status={e.status} />
                        {e.status === 'rejected' && (
                          <HelpTooltip text={
                            <span>
                              Rejected by manager
                              {e.rejection_reason && (
                                <><br /><span className="text-slate-300">Reason: {e.rejection_reason}</span></>
                              )}
                            </span>
                          } />
                        )}
                      </span>
                      {e.edited && <span className="ml-1.5 text-xs text-amber-600 font-medium">edited</span>}
                    </td>
                    {isManager && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {isPending && (
                            <>
                              <button
                                onClick={() => onApprove(e.id)}
                                disabled={isActioning}
                                title="Approve"
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-green-100 hover:bg-green-200 text-green-700 disabled:opacity-50 transition-colors"
                                data-testid={`approve-btn-${idx}`}
                              >
                                {isActioning
                                  ? <Loader2 size={12} className="animate-spin" />
                                  : <Check size={12} />}
                              </button>
                              <button
                                onClick={() => onReject(e)}
                                disabled={isActioning}
                                title="Reject"
                                className="w-7 h-7 flex items-center justify-center rounded-md bg-red-100 hover:bg-red-200 text-red-700 disabled:opacity-50 transition-colors"
                                data-testid={`reject-btn-${idx}`}
                              >
                                <X size={12} />
                              </button>
                            </>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => onEdit(e)}
                              disabled={isActioning}
                              title={isApproved ? 'Edit (resets to Pending Approval)' : 'Edit times'}
                              className={`w-7 h-7 flex items-center justify-center rounded-md disabled:opacity-50 transition-colors ${
                                isApproved
                                  ? 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                              }`}
                              data-testid={`edit-btn-${idx}`}
                            >
                              <Pencil size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td className="px-4 py-3 font-bold text-slate-800" colSpan={isManager ? 6 : 4}>Total</td>
                <td className="px-4 py-3 font-bold text-slate-900">
                  {fmtHours(entries.reduce((s, e) => s + (e.duration_hours || 0), 0))}
                </td>
                <td />
                {isManager && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Edit Time Modal ───────────────────────────────────────────────────────────

function EditTimeModal({ open, entry, onClose, onSave, loading, wageMap = {} }) {
  const { formatMoney } = useCurrency();
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [reason, setReason] = useState('');
  const [tipsAmount, setTipsAmount] = useState('');
  const [kpi, setKpi] = useState({
    tables_served: '', reviews_5_star: '', upsell_tables: '', complaints_count: '',
    checklist_opening: false, checklist_mid: false, checklist_closing: false, report_submitted: false,
  });
  const [savingKpi, setSavingKpi] = useState(false);
  const [showKpi, setShowKpi] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Reset fields when the modal opens with a new entry
  useEffect(() => {
    if (entry && open) {
      setClockIn(isoToLocalInput(entry.clock_in));
      setClockOut(isoToLocalInput(entry.clock_out));
      setReason('');
      setTipsAmount(entry.tips_amount ? String(entry.tips_amount) : '');
      setKpi({
        tables_served: entry.kpi_data?.tables_served ?? '',
        reviews_5_star: entry.kpi_data?.reviews_5_star ?? '',
        upsell_tables: entry.kpi_data?.upsell_tables ?? '',
        complaints_count: entry.kpi_data?.complaints_count ?? '',
        checklist_opening: !!entry.kpi_data?.checklist_opening,
        checklist_mid: !!entry.kpi_data?.checklist_mid,
        checklist_closing: !!entry.kpi_data?.checklist_closing,
        report_submitted: !!entry.kpi_data?.report_submitted,
      });
      setShowKpi(!!entry.kpi);
      setPreview(null);
    }
  }, [entry, open]);

  const saveKpi = async () => {
    if (!entry?.id) return;
    setSavingKpi(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(kpi).map(([k, v]) => [k, v === '' ? null : v]),
      );
      const res = await timesheetAPI.updateShiftKpi(entry.id, payload);
      toast.success(`KPI saved · Score ${res.data.kpi?.kpi_score ?? '—'} (${res.data.kpi?.status ?? '—'})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KPI save failed');
    } finally {
      setSavingKpi(false);
    }
  };

  // Live KPI preview — pure frontend, mirrors backend calculate_supervisor_kpi.
  // Returns null when there is no signal to score against.
  const kpiPreview = useMemo(() => {
    const tables = parseFloat(kpi.tables_served) || 0;
    const reviews = parseFloat(kpi.reviews_5_star) || 0;
    const upsells = parseFloat(kpi.upsell_tables) || 0;
    const complaintsRaw = kpi.complaints_count;
    const hasAny =
      kpi.tables_served !== '' ||
      kpi.reviews_5_star !== '' ||
      kpi.upsell_tables !== '' ||
      complaintsRaw !== '' ||
      kpi.checklist_opening || kpi.checklist_mid || kpi.checklist_closing ||
      kpi.report_submitted;
    if (!hasAny) return null;

    const reviewRate = tables > 0 ? reviews / tables : 0;
    const upsellRate = tables > 0 ? upsells / tables : 0;

    let score = 0;
    score += reviewRate >= 0.5 ? 25 : reviewRate * 50;       // 25 pts at 50% rate
    score += upsellRate >= 0.25 ? 25 : upsellRate * 100;      // 25 pts at 25% rate
    if ((parseInt(complaintsRaw, 10) || 0) === 0) score += 20;
    if (kpi.checklist_opening && kpi.checklist_mid && kpi.checklist_closing) score += 15;
    if (kpi.report_submitted) score += 15;

    score = Math.round(score);
    const status = score >= 85 ? 'GREEN' : score >= 70 ? 'YELLOW' : 'RED';
    return { score, status, bonus_eligible: score >= 85 };
  }, [kpi]);

  // Debounced payroll preview — fires 300 ms after the last change
  useEffect(() => {
    if (!clockIn || !clockOut) { setPreview(null); return; }
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const wage = entry ? (wageMap[entry.employee_id] ?? null) : null;
        const body = {
          clock_in: localInputToISO(clockIn),
          clock_out: localInputToISO(clockOut),
        };
        if (wage != null) body.hourly_wage = wage;
        const res = await timesheetAPI.payrollPreview(body);
        setPreview(res.data);
      } catch {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clockIn, clockOut, entry, wageMap]);

  if (!open || !entry) return null;

  const handleSave = () => {
    const data = {
      start_time: clockIn ? localInputToISO(clockIn) : undefined,
      end_time: clockOut ? localInputToISO(clockOut) : undefined,
      reason: reason.trim() || undefined,
      tips_amount: tipsAmount === '' ? undefined : Math.max(0, parseFloat(tipsAmount) || 0),
    };
    onSave(entry.id, data);
  };

  const wage = wageMap[entry.employee_id] ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="edit-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="edit-time-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Edit Time Entry
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="edit-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Employee info */}
          <div className="p-3 bg-slate-50 rounded-xl">
            <p className="text-sm font-semibold text-slate-800">{entry.employee_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{entry.date} · {entry.location_name}</p>
            {entry.status === 'approved' && (
              <p className="text-xs text-amber-600 font-medium mt-1.5">
                Approved entry — editing will reset to Pending Approval
              </p>
            )}
          </div>

          {/* Time inputs */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Clock In</Label>
            <Input
              type="datetime-local"
              value={clockIn}
              onChange={e => setClockIn(e.target.value)}
              className="h-10"
              data-testid="edit-modal-clock-in"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Clock Out</Label>
            <Input
              type="datetime-local"
              value={clockOut}
              onChange={e => setClockOut(e.target.value)}
              className="h-10"
              data-testid="edit-modal-clock-out"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
              Tips <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={tipsAmount}
              onChange={e => setTipsAmount(e.target.value)}
              className="h-10"
              data-testid="edit-modal-tips"
            />
          </div>

          {/* Supervisor KPI section (collapsible) */}
          <div className="rounded-md border border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <button
                type="button"
                onClick={() => setShowKpi(s => !s)}
                className="flex-1 flex items-center justify-between text-sm font-semibold text-slate-700 text-left"
                data-testid="kpi-section-toggle"
              >
                <span>Supervisor KPI {entry?.kpi ? <span className={`ml-2 px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                  entry.kpi.status === 'GREEN' ? 'bg-green-100 text-green-700' :
                  entry.kpi.status === 'YELLOW' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>{entry.kpi.kpi_score} ({entry.kpi.status})</span> : <span className="text-slate-400 font-normal text-xs ml-2">— optional</span>}</span>
                <span className="text-xs text-slate-400">{showKpi ? 'hide' : 'edit'}</span>
              </button>
              <a
                href="/help/kpi-bonus"
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-orange-600 hover:text-orange-700 hover:underline"
                data-testid="kpi-help-link"
                title="Open the KPI & Bonus guide in a new tab"
              >
                <BookOpen size={12} />
                Learn how KPI works
              </a>
            </div>
            {showKpi && (
              <div className="p-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="kpi-section-body">
                {[
                  ['tables_served', 'Tables'],
                  ['reviews_5_star', '5★ reviews'],
                  ['upsell_tables', 'Upsell tables'],
                  ['complaints_count', 'Complaints'],
                ].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-[11px] text-slate-500">{l}</Label>
                    <Input
                      type="number" min="0" step="1"
                      value={kpi[k]}
                      onChange={e => setKpi({ ...kpi, [k]: e.target.value })}
                      className="h-8 text-xs"
                      data-testid={`kpi-${k}`}
                    />
                  </div>
                ))}
                {[
                  ['checklist_opening', 'Opening checklist'],
                  ['checklist_mid', 'Mid-shift checklist'],
                  ['checklist_closing', 'Closing checklist'],
                  ['report_submitted', 'Report submitted'],
                ].map(([k, l]) => (
                  <label key={k} className="col-span-2 sm:col-span-1 flex items-center gap-2 text-xs text-slate-600 mt-1">
                    <input
                      type="checkbox"
                      checked={!!kpi[k]}
                      onChange={e => setKpi({ ...kpi, [k]: e.target.checked })}
                      data-testid={`kpi-${k}`}
                      className="rounded"
                    />
                    {l}
                  </label>
                ))}
                <div className="col-span-2 sm:col-span-4 flex items-center justify-between gap-3 pt-1 flex-wrap">
                  {kpiPreview ? (
                    <div
                      className={`text-xs font-semibold flex items-center gap-2 ${
                        kpiPreview.status === 'GREEN'
                          ? 'text-green-700'
                          : kpiPreview.status === 'YELLOW'
                          ? 'text-amber-700'
                          : 'text-red-700'
                      }`}
                      data-testid="kpi-live-preview"
                    >
                      <span>KPI Score: {kpiPreview.score} ({kpiPreview.status})</span>
                      {kpiPreview.bonus_eligible && (
                        <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">
                          Bonus Eligible
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-400" data-testid="kpi-live-empty">
                      Enter values to preview score
                    </span>
                  )}
                  <Button
                    type="button" size="sm"
                    onClick={saveKpi}
                    disabled={savingKpi || !kpiPreview}
                    title={!kpiPreview ? 'Enter KPI data first' : ''}
                    className="h-7 px-3 text-xs bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="kpi-save-btn"
                  >
                    {savingKpi ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
                    Save KPI
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ── Estimated Pay card ── */}
          {(clockIn && clockOut) && (
            <div
              className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50 p-4"
              data-testid="payroll-preview-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <DollarSign size={13} className="text-teal-600" />
                  <span className="text-xs font-semibold text-teal-800 uppercase tracking-wide">
                    Estimated Pay
                  </span>
                </div>
                {previewLoading && (
                  <Loader2 size={12} className="animate-spin text-teal-400" data-testid="preview-spinner" />
                )}
              </div>

              {/* Skeleton while first load */}
              {previewLoading && !preview ? (
                <div className="grid grid-cols-2 gap-2" data-testid="preview-skeleton">
                  {[0,1,2,3].map(i => (
                    <div key={i} className="h-12 bg-teal-100/70 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : preview ? (
                <>
                  {/* Hours grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2" data-testid="preview-hours-grid">
                    <div className="bg-white/80 rounded-lg px-3 py-2.5" data-testid="preview-regular-hours">
                      <p className="text-slate-400 mb-0.5">Regular</p>
                      <p className="font-semibold text-slate-800">{fmtHours(preview.regular_hours)}</p>
                    </div>
                    <div className="bg-white/80 rounded-lg px-3 py-2.5" data-testid="preview-overtime-hours">
                      <p className="text-slate-400 mb-0.5">Overtime</p>
                      <p className={`font-semibold ${preview.overtime_hours > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                        {fmtHours(preview.overtime_hours)}
                      </p>
                    </div>
                    {preview.holiday_hours > 0 && (
                      <div className="bg-white/80 rounded-lg px-3 py-2.5" data-testid="preview-holiday-hours">
                        <p className="text-slate-400 mb-0.5">Holiday</p>
                        <p className="font-semibold text-violet-700">{fmtHours(preview.holiday_hours)}</p>
                      </div>
                    )}
                    {preview.break_hours > 0 && (
                      <div className="bg-white/80 rounded-lg px-3 py-2.5" data-testid="preview-break-hours">
                        <p className="text-slate-400 mb-0.5">Unpaid Break</p>
                        <p className="font-semibold text-slate-500">−{fmtHours(preview.break_hours)}</p>
                      </div>
                    )}
                  </div>

                  {/* Total Pay row */}
                  {preview.total_pay != null ? (
                    <div
                      className="flex items-center justify-between bg-teal-700 rounded-lg px-3 py-2.5 text-white"
                      data-testid="preview-total-pay"
                    >
                      <span className="text-xs font-medium">Total Pay</span>
                      <span className="font-bold text-sm">{formatMoney(preview.total_pay)}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 text-center" data-testid="preview-no-wage">
                      Set hourly wage in{' '}
                      <a href="/staff" className="underline text-teal-600">Staff</a>{' '}
                      to see pay estimate
                    </p>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Audit note */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
              Audit Note <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Corrected missed clock-out, payroll adjustment..."
              rows={2}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
              data-testid="edit-modal-reason"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-6">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="edit-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || (!clockIn && !clockOut)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="edit-modal-save"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

function RejectModal({ open, entry, onClose, onReject, loading }) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!open || !entry) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="reject-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
        data-testid="reject-time-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Reject Time Entry
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="reject-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4">
          <div className="p-3 bg-slate-50 rounded-xl mb-5">
            <p className="text-sm font-semibold text-slate-800">{entry.employee_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {entry.date}
              {entry.duration_hours > 0 && ` · ${fmtHours(entry.duration_hours)}`}
            </p>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
              Rejection Reason <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Optional note explaining why this entry was rejected."
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition-all"
              data-testid="reject-modal-reason"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-6">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="reject-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onReject(entry.id, reason)}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="reject-modal-confirm"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <X className="h-4 w-4 mr-1.5" />}
            Reject Entry
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Payroll Summary view ──────────────────────────────────────────────────────

function PayrollSummaryView({ data, entries = [], filters, onExportCSV, onExportExcel, exporting }) {
  const { formatMoney } = useCurrency();
  const { employees = [], period_totals = {}, overtime_threshold_hours_per_day = 8, has_holiday_data = false } = data;
  const [drillEmp, setDrillEmp] = useState(null);

  if (employees.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-16 text-center">
          <DollarSign className="mx-auto h-12 w-12 text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No payroll data for this period</p>
          <p className="text-slate-400 text-sm mt-1">Only completed (clocked-out) shifts are included in payroll</p>
        </CardContent>
      </Card>
    );
  }

  const hasWages = employees.some(e => e.hourly_wage != null);
  const hasHoliday = has_holiday_data || (period_totals.holiday_hours ?? 0) > 0;
  const hasTips = (period_totals.tips ?? 0) > 0 || employees.some(e => (e.tips ?? 0) > 0);
  const hasBonus = (period_totals.bonus ?? 0) > 0 || employees.some(e => (e.bonus ?? 0) > 0);
  const wagesMissing = period_totals.wages_missing || 0;

  const COL = {
    regular: { header: 'bg-emerald-600 text-white', cell: 'bg-emerald-50 text-emerald-800 font-medium' },
    overtime: { header: 'bg-amber-500 text-white', cell: 'bg-amber-50 text-amber-800 font-medium' },
    holiday: { header: 'bg-violet-600 text-white', cell: 'bg-violet-50 text-violet-800 font-medium' },
    pto: { header: 'bg-sky-500 text-white', cell: 'bg-sky-50 text-sky-700' },
    unpaid: { header: 'bg-slate-400 text-white', cell: 'bg-slate-50 text-slate-500' },
    total: { header: 'bg-teal-600 text-white', cell: 'bg-teal-50 text-teal-800 font-semibold' },
    pay: { header: 'bg-violet-600 text-white', cell: 'bg-violet-50 text-violet-800 font-semibold' },
  };

  return (
    <div className="space-y-4" data-testid="payroll-summary-view">

      {/* stats banner */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4"
           data-testid="payroll-stats-banner">
        <StatPill label="Regular" value={fmtHours(period_totals.regular_hours ?? 0)} color="emerald" />
        <span className="text-slate-300 font-bold text-lg">+</span>
        <StatPill label="Overtime" value={fmtHours(period_totals.overtime_hours ?? 0)} color="amber" />
        {hasHoliday && (
          <>
            <span className="text-slate-300 font-bold text-lg">+</span>
            <StatPill label="Holiday" value={fmtHours(period_totals.holiday_hours ?? 0)} color="violet" data-testid="holiday-stat-pill" />
          </>
        )}
        <span className="text-slate-300 font-bold text-lg">+</span>
        <StatPill label="Paid Time Off" value={fmtHours(period_totals.pto_hours ?? 0)} color="sky" />
        <span className="text-slate-300 font-bold text-lg">=</span>
        <StatPill label="Total Paid Hours" value={fmtHours(period_totals.total_hours ?? 0)} color="teal" large />
        {hasWages && period_totals.total_pay > 0 && (
          <>
            <span className="text-slate-200 font-bold text-lg hidden sm:inline">|</span>
            <StatPill
              label="Est. Total Pay"
              value={formatMoney(period_totals.total_pay ?? 0)}
              color="violet"
              large
              data-testid="payroll-total-pay-pill"
            />
          </>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
          <Clock size={12} />
          OT threshold: &gt;{overtime_threshold_hours_per_day}h/day
        </div>
      </div>

      {/* Wages missing warning */}
      {wagesMissing > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="wages-missing-banner">
          <span className="mt-0.5 shrink-0">⚠</span>
          <span>
            <strong>{wagesMissing} employee{wagesMissing > 1 ? 's' : ''}</strong> don't have an hourly wage set — pay columns show <strong>—</strong> for them.
            {' '}<a href="/staff" className="underline font-medium">Set wages in Staff</a>.
          </span>
        </div>
      )}

      {/* Tips + Bonuses quick totals */}
      <div className="flex gap-4 flex-wrap text-sm text-slate-600" data-testid="payroll-extras-totals">
        <div data-testid="payroll-total-tips">Total Tips: <span className="font-semibold text-slate-800">{formatMoney(period_totals.tips ?? 0)}</span></div>
        <div data-testid="payroll-total-bonuses">Total Bonuses: <span className="font-semibold text-slate-800">{formatMoney(period_totals.bonus ?? 0)}</span></div>
      </div>

      {/* sub-header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-500">
          <span className="font-medium text-slate-700">{employees.length} employee{employees.length !== 1 ? 's' : ''}</span>
          {' '}— {filters.startDate} to {filters.endDate}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onExportCSV}
            disabled={!!exporting} data-testid="payroll-export-csv-btn">
            {exporting === 'payroll-csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
            CSV
          </Button>
          <Button size="sm" onClick={onExportExcel}
            disabled={!!exporting}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="payroll-export-excel-btn">
            {exporting === 'payroll-excel' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1" />}
            Export Payroll
          </Button>
        </div>
      </div>

      {/* main payroll table */}
      <Card className="border-slate-200" data-testid="payroll-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3 bg-slate-800 text-white font-semibold rounded-tl-lg">Employee</th>
                  <th className="text-left px-4 py-3 bg-slate-700 text-white font-semibold">Location(s)</th>
                  <th className={`text-center px-4 py-3 font-semibold ${COL.regular.header}`}>Regular Hours</th>
                  <th className={`text-center px-4 py-3 font-semibold ${COL.overtime.header}`}>OT Hours</th>
                  {hasHoliday && (
                    <th className={`text-center px-4 py-3 font-semibold ${COL.holiday.header}`} data-testid="holiday-hours-col">Holiday Hours</th>
                  )}
                  <th className={`text-center px-4 py-3 font-semibold ${COL.total.header}${!hasWages && !hasHoliday ? ' rounded-tr-lg' : ''}`}>Total Hours</th>
                  {hasWages && <>
                    <th className="text-center px-4 py-3 bg-slate-600 text-white font-semibold">Rate</th>
                    <th className={`text-center px-4 py-3 font-semibold ${COL.pay.header}`}>Regular Pay</th>
                    <th className={`text-center px-4 py-3 font-semibold bg-amber-700 text-white`}>OT Pay</th>
                    {hasHoliday && (
                      <th className={`text-center px-4 py-3 font-semibold ${COL.holiday.header}`} data-testid="holiday-pay-col">Holiday Pay</th>
                    )}
                    {hasTips && (
                      <th className="text-center px-4 py-3 font-semibold bg-emerald-700 text-white" data-testid="tips-col">Tips</th>
                    )}
                    {hasBonus && (
                      <th className="text-center px-4 py-3 font-semibold bg-violet-700 text-white" data-testid="bonus-col">Bonus</th>
                    )}
                    <th className={`text-center px-4 py-3 font-semibold ${COL.pay.header} rounded-tr-lg`}>Total Pay</th>
                  </>}
                  {!hasWages && hasHoliday && <th className={`text-center px-4 py-3 font-semibold ${COL.total.header} rounded-tr-lg`}></th>}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => (
                  <tr key={emp.employee_id || idx}
                      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                      data-testid={`payroll-row-${idx}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      <button
                        className="text-left hover:text-teal-700 underline decoration-dotted underline-offset-2 transition-colors focus:outline-none focus:text-teal-700"
                        onClick={() => setDrillEmp(emp)}
                        data-testid={`payroll-row-name-${idx}`}
                        title="View breakdown"
                      >
                        {emp.employee_name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs max-w-[160px] truncate" title={emp.location}>
                      {emp.location || '—'}
                    </td>
                    <td className={`px-4 py-3 text-center ${COL.regular.cell}`}>
                      {emp.regular_hours > 0 ? fmtHours(emp.regular_hours) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-center ${COL.overtime.cell}`}>
                      {emp.overtime_hours > 0 ? fmtHours(emp.overtime_hours) : '—'}
                    </td>
                    {hasHoliday && (
                      <td className={`px-4 py-3 text-center ${COL.holiday.cell}`} data-testid={`holiday-hours-${idx}`}>
                        {(emp.holiday_hours ?? 0) > 0 ? fmtHours(emp.holiday_hours) : '—'}
                      </td>
                    )}
                    <td className={`px-4 py-3 text-center text-base ${COL.total.cell}`}>
                      {emp.total_hours > 0 ? fmtHours(emp.total_hours) : '—'}
                    </td>
                    {hasWages && <>
                      <td className="px-4 py-3 text-center text-slate-600 font-medium text-xs">
                        {emp.hourly_wage != null ? formatMoney(emp.hourly_wage) + '/hr' : '—'}
                      </td>
                      <td className={`px-4 py-3 text-center ${COL.pay.cell}`} data-testid={`regular-pay-${idx}`}>
                        {emp.regular_pay != null ? formatMoney(emp.regular_pay) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center bg-amber-50 text-amber-800 font-semibold" data-testid={`ot-pay-${idx}`}>
                        {emp.overtime_pay != null ? formatMoney(emp.overtime_pay) : '—'}
                      </td>
                      {hasHoliday && (
                        <td className={`px-4 py-3 text-center ${COL.holiday.cell}`} data-testid={`holiday-pay-${idx}`}>
                          {emp.holiday_pay != null ? formatMoney(emp.holiday_pay) : '—'}
                        </td>
                      )}
                      {hasTips && (
                        <td className="px-4 py-3 text-center bg-emerald-50 text-emerald-800 font-semibold" data-testid={`tips-${idx}`}>
                          {(emp.tips ?? 0) > 0 ? formatMoney(emp.tips) : '—'}
                        </td>
                      )}
                      {hasBonus && (
                        <td className="px-4 py-3 text-center bg-violet-50 text-violet-800 font-semibold" data-testid={`bonus-${idx}`}
                            title={`${emp.bonus_shifts || 0} bonus-eligible shift${(emp.bonus_shifts || 0) === 1 ? '' : 's'}`}>
                          {(emp.bonus ?? 0) > 0 ? formatMoney(emp.bonus) : '—'}
                        </td>
                      )}
                      <td className={`px-4 py-3 text-center text-base ${COL.pay.cell}`} data-testid={`total-pay-${idx}`}>
                        {emp.total_pay != null ? formatMoney(emp.total_pay) : '—'}
                      </td>
                    </>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-teal-700 text-white">
                  <td className="px-4 py-3 font-bold" colSpan={2}>Period Total</td>
                  <td className="px-4 py-3 text-center font-bold">
                    {fmtHours(period_totals.regular_hours ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-center font-bold">
                    {fmtHours(period_totals.overtime_hours ?? 0)}
                  </td>
                  {hasHoliday && (
                    <td className="px-4 py-3 text-center font-bold" data-testid="holiday-hours-footer">
                      {fmtHours(period_totals.holiday_hours ?? 0)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-center font-bold text-base">
                    {fmtHours(period_totals.total_hours ?? 0)}
                  </td>
                  {hasWages && <>
                    <td className="px-4 py-3 text-center font-bold">—</td>
                    <td className="px-4 py-3 text-center font-bold">
                      {period_totals.regular_pay > 0 ? formatMoney(period_totals.regular_pay) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center font-bold">
                      {period_totals.overtime_pay > 0 ? formatMoney(period_totals.overtime_pay) : '—'}
                    </td>
                    {hasHoliday && (
                      <td className="px-4 py-3 text-center font-bold" data-testid="holiday-pay-footer">
                        {(period_totals.holiday_pay ?? 0) > 0 ? formatMoney(period_totals.holiday_pay) : '—'}
                      </td>
                    )}
                    {hasTips && (
                      <td className="px-4 py-3 text-center font-bold" data-testid="tips-footer">
                        {(period_totals.tips ?? 0) > 0 ? formatMoney(period_totals.tips) : '—'}
                      </td>
                    )}
                    {hasBonus && (
                      <td className="px-4 py-3 text-center font-bold" data-testid="bonus-footer">
                        {(period_totals.bonus ?? 0) > 0 ? formatMoney(period_totals.bonus) : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-center font-bold text-base" data-testid="payroll-total-pay-footer">
                      {period_totals.total_pay > 0 ? formatMoney(period_totals.total_pay) : '—'}
                    </td>
                  </>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        * Regular hours: up to {overtime_threshold_hours_per_day}h/day. Overtime: above {overtime_threshold_hours_per_day}h/day.
        {hasHoliday ? ' Holiday hours: shifts on configured holiday dates (holiday multiplier applied).' : ''}
        {' '}Pay estimates based on configured hourly wages. Only completed (clocked-out) shifts are included.
        {' '}<span className="text-teal-600">Click an employee name to see their breakdown.</span>
      </p>

      {drillEmp && (
        <PayrollBreakdownModal
          emp={drillEmp}
          filters={filters}
          entries={entries}
          otThreshold={overtime_threshold_hours_per_day}
          holidayDates={data.holiday_dates || []}
          onClose={() => setDrillEmp(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PayrollBreakdownModal — read-only per-employee breakdown from Payroll Summary
// ---------------------------------------------------------------------------

function PayrollBreakdownModal({ emp, filters, entries = [], otThreshold = 8, holidayDates = [], onClose }) {
  const { formatMoney } = useCurrency();

  // ── Holiday set: O(1) lookup by date string ─────────────────────────────
  const holidaySet = useMemo(
    () => new Set((holidayDates || []).map(h => h.date).filter(Boolean)),
    [holidayDates]
  );

  // ── Compute per-day hours for this employee from already-loaded entries ──
  const dailyData = useMemo(() => {
    const dayMap = {};
    entries
      .filter(e => e.employee_id === emp.employee_id && (e.duration_hours ?? 0) > 0)
      .forEach(e => {
        if (e.date) dayMap[e.date] = (dayMap[e.date] || 0) + e.duration_hours;
      });
    return Object.entries(dayMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, hours]) => {
        const d = new Date(date + 'T00:00:00');
        return {
          date,
          label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          hours: parseFloat(hours.toFixed(2)),
          isHoliday: holidaySet.has(date),
        };
      });
  }, [entries, emp.employee_id, holidaySet]);

  // ── Color priority: holiday > overtime > regular ─────────────────────────
  const barColor = (d) => {
    if (d.isHoliday) return '#8b5cf6';   // violet-500 — holiday
    if (d.hours > otThreshold) return '#f97316';  // orange-500 — overtime
    return '#14b8a6';                              // teal-500   — regular
  };

  // Legend: show only the categories that actually appear in the data
  const showOT      = dailyData.some(d => !d.isHoliday && d.hours > otThreshold);
  const showHoliday = dailyData.some(d => d.isHoliday);

  const hasWage = emp.hourly_wage != null;
  const hasOT = (emp.overtime_hours ?? 0) > 0;
  const hasHoliday = (emp.holiday_hours ?? 0) > 0;
  const hasBreak = (emp.unpaid_hours ?? 0) > 0;

  const chartWidth = Math.max(280, dailyData.length * 36);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="breakdown-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md sm:mx-auto max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="payroll-breakdown-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              {emp.employee_name}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {filters.startDate} → {filters.endDate}
              {emp.shift_count != null && ` · ${emp.shift_count} shift${emp.shift_count !== 1 ? 's' : ''}`}
              {emp.location && ` · ${emp.location}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            data-testid="breakdown-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">

          {/* ── Daily hours bar chart ── */}
          {dailyData.length > 0 && (
            <div data-testid="breakdown-chart">
              {/* Title + legend */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Daily Hours
                </p>
                <div className="flex items-center gap-3 text-xs text-slate-500" data-testid="chart-legend">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-teal-500" data-testid="legend-regular" />
                    Regular
                  </span>
                  {showOT && (
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm bg-orange-500" data-testid="legend-overtime" />
                      Overtime
                    </span>
                  )}
                  {showHoliday && (
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm bg-violet-500" data-testid="legend-holiday" />
                      Holiday
                    </span>
                  )}
                </div>
              </div>

              {/* Chart */}
              <div className="overflow-x-auto rounded-xl border border-slate-100 bg-slate-50 py-2">
                <div style={{ width: chartWidth, height: 120 }}>
                  <BarChart
                    width={chartWidth}
                    height={120}
                    data={dailyData}
                    barSize={20}
                    margin={{ top: 4, right: 10, left: -16, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 9, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 9, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                      tickCount={4}
                      width={28}
                    />
                    <ReTooltip
                      formatter={(v, _name, props) => {
                        const d = props.payload;
                        const label = d.isHoliday ? 'Holiday' : (v > otThreshold ? 'Overtime' : 'Regular');
                        return [`${fmtHours(v)} (${label})`, 'Hours'];
                      }}
                      contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 10px' }}
                      cursor={{ fill: 'rgba(20,184,166,0.08)' }}
                    />
                    <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                      {dailyData.map((d, i) => (
                        <Cell
                          key={`cell-${i}`}
                          fill={barColor(d)}
                          data-testid={d.isHoliday ? 'bar-holiday' : (d.hours > otThreshold ? 'bar-ot' : 'bar-regular')}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </div>
              </div>
            </div>
          )}

          {/* Hours grid */}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Hours Breakdown</p>
          <div className="grid grid-cols-2 gap-2 text-xs" data-testid="breakdown-hours-grid">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="breakdown-regular-hours">
              <p className="text-emerald-600 mb-0.5">Regular</p>
              <p className="font-bold text-emerald-900 text-sm">{fmtHours(emp.regular_hours ?? 0)}</p>
            </div>
            <div className={`${hasOT ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'} border rounded-xl px-4 py-3`}
                 data-testid="breakdown-overtime-hours">
              <p className={`mb-0.5 ${hasOT ? 'text-amber-600' : 'text-slate-400'}`}>Overtime</p>
              <p className={`font-bold text-sm ${hasOT ? 'text-amber-900' : 'text-slate-400'}`}>
                {fmtHours(emp.overtime_hours ?? 0)}
              </p>
            </div>
            {hasHoliday && (
              <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3" data-testid="breakdown-holiday-hours">
                <p className="text-violet-600 mb-0.5">Holiday</p>
                <p className="font-bold text-violet-900 text-sm">{fmtHours(emp.holiday_hours)}</p>
              </div>
            )}
            {hasBreak && (
              <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3" data-testid="breakdown-break-hours">
                <p className="text-slate-400 mb-0.5">Unpaid Break</p>
                <p className="font-bold text-slate-500 text-sm">−{fmtHours(emp.unpaid_hours)}</p>
              </div>
            )}
          </div>

          {/* Total hours pill */}
          <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-xl px-4 py-3"
               data-testid="breakdown-total-hours">
            <span className="text-xs font-medium text-teal-700">Total Paid Hours</span>
            <span className="font-bold text-teal-900 text-sm">{fmtHours(emp.total_hours ?? 0)}</span>
          </div>

          {/* Pay section */}
          {hasWage ? (
            <>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">Pay Breakdown</p>
              <div className="grid grid-cols-2 gap-2 text-xs" data-testid="breakdown-pay-grid">
                <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3" data-testid="breakdown-regular-pay">
                  <p className="text-violet-500 mb-0.5">Regular Pay</p>
                  <p className="font-bold text-violet-900 text-sm">{formatMoney(emp.regular_pay ?? 0)}</p>
                </div>
                <div className={`${hasOT ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'} border rounded-xl px-4 py-3`}
                     data-testid="breakdown-overtime-pay">
                  <p className={`mb-0.5 ${hasOT ? 'text-amber-600' : 'text-slate-400'}`}>OT Pay</p>
                  <p className={`font-bold text-sm ${hasOT ? 'text-amber-900' : 'text-slate-400'}`}>
                    {emp.overtime_pay != null ? formatMoney(emp.overtime_pay) : '—'}
                  </p>
                </div>
                {hasHoliday && emp.holiday_pay != null && (
                  <div className="bg-violet-50 border border-violet-100 rounded-xl px-4 py-3" data-testid="breakdown-holiday-pay">
                    <p className="text-violet-500 mb-0.5">Holiday Pay</p>
                    <p className="font-bold text-violet-900 text-sm">{formatMoney(emp.holiday_pay)}</p>
                  </div>
                )}
                {emp.hourly_wage != null && (
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3" data-testid="breakdown-wage-rate">
                    <p className="text-slate-400 mb-0.5">Hourly Rate</p>
                    <p className="font-bold text-slate-700 text-sm">{formatMoney(emp.hourly_wage)}/hr</p>
                  </div>
                )}
              </div>
              <div
                className="flex items-center justify-between bg-teal-700 rounded-xl px-4 py-3 text-white"
                data-testid="breakdown-total-pay"
              >
                <span className="text-xs font-medium">Total Estimated Pay</span>
                <span className="font-bold text-base">{formatMoney(emp.total_pay ?? 0)}</span>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400 text-center py-2" data-testid="breakdown-no-wage">
              No hourly wage set —{' '}
              <a href="/staff" className="underline text-teal-600">configure in Staff</a>{' '}
              to see pay estimate.
            </p>
          )}
        </div>

        <div className="px-6 pb-6">
          <Button
            variant="outline"
            className="w-full"
            onClick={onClose}
            data-testid="breakdown-modal-done"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, color, large = false, 'data-testid': testId }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    teal: 'bg-teal-50 border-teal-200 text-teal-800',
  };
  return (
    <div data-testid={testId} className={`flex flex-col items-center border rounded-lg px-4 py-2 ${colors[color] || ''}`}>
      <span className={`font-bold ${large ? 'text-xl' : 'text-lg'}`}>{value}</span>
      <span className="text-xs opacity-75 mt-0.5">{label}</span>
    </div>
  );
}


// ── Time Corrections (manager review) ────────────────────────────────────────
const CORRECTION_TYPE_LABELS = {
  forgot_clock_in:     'Forgot Clock In',
  forgot_clock_out:    'Forgot Clock Out',
  incorrect_clock_in:  'Incorrect Clock In',
  incorrect_clock_out: 'Incorrect Clock Out',
  break_missing:       'Break Missing',
  other:               'Other',
};

const CORRECTION_STATUS_STYLES = {
  pending:  { dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200',  label: 'Pending'  },
  approved: { dot: 'bg-green-500',  text: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200',   label: 'Approved' },
  rejected: { dot: 'bg-rose-500',   text: 'text-rose-700',   bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'Rejected' },
};

function fmtCorrectionValue(v) {
  if (!v) return '—';
  // ISO datetime → local "MMM D, h:mma"; otherwise return as-is.
  const d = new Date(v);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}T/.test(String(v))) {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  return v;
}

function CorrectionsView({ corrections, statusFilter, onStatusFilterChange, onRefresh, onRefreshEntries }) {
  const [reviewModal, setReviewModal] = useState({ open: false, mode: null, request: null });

  const counts = useMemo(() => ({
    pending:  corrections.filter(c => c.status === 'pending').length,
    approved: corrections.filter(c => c.status === 'approved').length,
    rejected: corrections.filter(c => c.status === 'rejected').length,
  }), [corrections]);

  const filtered = useMemo(() => {
    const rows = corrections.filter(c => c.status === statusFilter);
    return rows.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [corrections, statusFilter]);

  const FILTERS = [
    { key: 'pending',  label: 'Pending',  count: counts.pending  },
    { key: 'approved', label: 'Approved', count: counts.approved },
    { key: 'rejected', label: 'Rejected', count: counts.rejected },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="corrections-view">
      {/* status filter chips */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
        {FILTERS.map(f => {
          const active = statusFilter === f.key;
          const s = CORRECTION_STATUS_STYLES[f.key];
          return (
            <button
              key={f.key}
              onClick={() => onStatusFilterChange(f.key)}
              data-testid={`correction-filter-${f.key}`}
              className={`text-xs font-medium border rounded-full px-3 py-1.5 transition-colors flex items-center gap-1.5 ${
                active
                  ? `${s.bg} ${s.text} ${s.border}`
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {f.label}
              <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/70' : 'bg-slate-100'}`}>
                {f.count}
              </span>
            </button>
          );
        })}
        <div className="ml-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            data-testid="corrections-refresh-btn"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Employee</th>
              <th className="text-left px-4 py-3 font-medium">Shift Date</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Original → Requested</th>
              <th className="text-left px-4 py-3 font-medium">Reason</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Manager Notes</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm" data-testid="corrections-empty">
                  No {statusFilter} requests
                </td>
              </tr>
            )}
            {filtered.map(c => {
              const s = CORRECTION_STATUS_STYLES[c.status] || CORRECTION_STATUS_STYLES.pending;
              return (
                <tr key={c.id} data-testid={`correction-row-${c.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{c.employee_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{c.shift_date || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {CORRECTION_TYPE_LABELS[c.correction_type] || c.correction_type}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="flex flex-col gap-0.5 text-xs">
                      <span className="text-slate-400 line-through">{fmtCorrectionValue(c.original_value)}</span>
                      <span className="text-slate-700 font-medium">{fmtCorrectionValue(c.approved_value || c.requested_value)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px]">
                    <div className="truncate" title={c.reason}>{c.reason}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${s.bg} ${s.text} ${s.border}`}
                      data-testid={`correction-status-${c.status}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 max-w-[200px]">
                    <div className="truncate text-xs" title={c.manager_notes || ''}>
                      {c.manager_notes || (c.status === 'pending' ? '—' : '')}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {c.status === 'pending' ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white h-7 px-2.5 text-xs"
                          onClick={() => setReviewModal({ open: true, mode: 'approve', request: c })}
                          data-testid={`correction-approve-btn-${c.id}`}
                        >
                          <Check size={12} className="mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-200 text-rose-700 hover:bg-rose-50 h-7 px-2.5 text-xs"
                          onClick={() => setReviewModal({ open: true, mode: 'reject', request: c })}
                          data-testid={`correction-reject-btn-${c.id}`}
                        >
                          <X size={12} className="mr-1" /> Reject
                        </Button>
                      </div>
                    ) : (
                      <div className="text-right text-[11px] text-slate-400">
                        by {c.reviewed_by_name || '—'}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100">
        {filtered.length === 0 && (
          <div className="px-4 py-10 text-center text-slate-400 text-sm">No {statusFilter} requests</div>
        )}
        {filtered.map(c => {
          const s = CORRECTION_STATUS_STYLES[c.status] || CORRECTION_STATUS_STYLES.pending;
          return (
            <div key={c.id} className="px-4 py-3 space-y-2" data-testid={`correction-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-800 text-sm">{c.employee_name || '—'}</div>
                  <div className="text-xs text-slate-500">
                    {c.shift_date || '—'} · {CORRECTION_TYPE_LABELS[c.correction_type] || c.correction_type}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2 py-0.5 ${s.bg} ${s.text} ${s.border}`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  {s.label}
                </span>
              </div>
              <div className="text-xs">
                <span className="text-slate-400 line-through">{fmtCorrectionValue(c.original_value)}</span>
                <span className="mx-1.5 text-slate-400">→</span>
                <span className="text-slate-700 font-medium">{fmtCorrectionValue(c.approved_value || c.requested_value)}</span>
              </div>
              <div className="text-xs text-slate-600">{c.reason}</div>
              {c.manager_notes && (
                <div className="text-xs text-slate-500 italic">Note: {c.manager_notes}</div>
              )}
              {c.status === 'pending' && (
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white flex-1 h-8 text-xs"
                    onClick={() => setReviewModal({ open: true, mode: 'approve', request: c })}
                    data-testid={`correction-approve-mob-${c.id}`}
                  >
                    <Check size={12} className="mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50 flex-1 h-8 text-xs"
                    onClick={() => setReviewModal({ open: true, mode: 'reject', request: c })}
                    data-testid={`correction-reject-mob-${c.id}`}
                  >
                    <X size={12} className="mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <CorrectionReviewModal
        open={reviewModal.open}
        mode={reviewModal.mode}
        request={reviewModal.request}
        onClose={() => setReviewModal({ open: false, mode: null, request: null })}
        onDone={() => { onRefresh(); onRefreshEntries(); }}
      />
    </div>
  );
}

function CorrectionReviewModal({ open, mode, request, onClose, onDone }) {
  const isApprove = mode === 'approve';
  const [approvedValue, setApprovedValue] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && request) {
      setApprovedValue(request.requested_value || '');
      setNotes('');
    }
  }, [open, request]);

  if (!request) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isApprove) {
        const payload = {};
        if (approvedValue && approvedValue !== request.requested_value) {
          payload.approved_value = approvedValue;
        }
        if (notes.trim()) payload.manager_notes = notes.trim();
        await timeCorrectionsAPI.approve(request.id, payload);
        toast.success('Correction approved');
      } else {
        const payload = {};
        if (notes.trim()) payload.manager_notes = notes.trim();
        await timeCorrectionsAPI.reject(request.id, payload);
        toast.success('Correction rejected');
      }
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="correction-review-modal">
        <DialogHeader>
          <DialogTitle data-testid="correction-review-title">
            {isApprove ? 'Approve' : 'Reject'} Correction Request
          </DialogTitle>
          <DialogDescription>
            {request.employee_name} · {request.shift_date || '—'} ·{' '}
            {CORRECTION_TYPE_LABELS[request.correction_type] || request.correction_type}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg bg-slate-50 p-3 text-xs space-y-1.5 border border-slate-200">
            <div className="flex justify-between">
              <span className="text-slate-500">Original:</span>
              <span className="font-medium text-slate-700">{fmtCorrectionValue(request.original_value)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Requested:</span>
              <span className="font-medium text-slate-700">{fmtCorrectionValue(request.requested_value)}</span>
            </div>
            <div className="border-t border-slate-200 pt-1.5">
              <span className="text-slate-500">Reason:</span>
              <p className="text-slate-700 mt-0.5">{request.reason}</p>
            </div>
          </div>

          {isApprove && (
            <div className="space-y-1.5">
              <Label htmlFor="approved-value">
                Approved Value <span className="text-slate-400 font-normal">(optional override)</span>
              </Label>
              <Input
                id="approved-value"
                value={approvedValue}
                onChange={(e) => setApprovedValue(e.target.value)}
                placeholder="ISO datetime or HH:MM"
                data-testid="correction-approved-value-input"
              />
              <p className="text-[11px] text-slate-500">
                Leave unchanged to use the requested value.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="manager-notes">
              Manager Notes <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="manager-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isApprove ? 'e.g. Verified with team lead' : 'e.g. No record of work performed'}
              maxLength={2000}
              data-testid="correction-notes-input"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className={`w-full sm:w-auto text-white ${
              isApprove ? 'bg-green-600 hover:bg-green-700' : 'bg-rose-600 hover:bg-rose-700'
            }`}
            data-testid="correction-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (isApprove ? <Check size={14} className="mr-1" /> : <X size={14} className="mr-1" />)}
            {isApprove ? 'Approve Request' : 'Reject Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
